import { useState, useEffect, useCallback } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import StatCard from '../../components/StatCard/StatCard';
import TableCard from '../../components/TableCard/TableCard';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import Drawer from '../../components/Drawer/Drawer';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { fetchPayments, updatePayment } from '../../lib/directus';
import styles from './Settlements.module.css';

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '-';
}

function mapPayment(p) {
  const order   = p.order || {};
  const partner = p.partner || {};

  return {
    id:        p.id,
    paymentId: p.payment_id ? `#${p.payment_id}` : `#PAY-${String(p.id).slice(-6).toUpperCase()}`,
    orderId:   order.order_id ? `#${order.order_id}` : order.id ? `#ORD-${String(order.id).padStart(6, '0')}` : '-',
    direction: p.direction || '-',
    partner:   partner.company_name || p.partner_name || '-',
    amount:    p.amount != null ? `R${parseFloat(p.amount).toLocaleString()}` : '-',
    status:    p.status || 'pending',
  };
}

export default function Settlements() {
  const [payments, setPayments]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('All status');
  const [selected, setSelected]         = useState([]);
  const [activePayment, setActive]      = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchPayments()
      .then(data => setPayments(data.map(mapPayment)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCount    = payments.length;
  const pendingCount  = payments.filter(p => p.status?.toLowerCase() === 'pending').length;
  const approvedCount = payments.filter(p => p.status?.toLowerCase() === 'approved').length;

  const filtered = payments.filter(row => {
    const matchSearch =
      row.paymentId.toLowerCase().includes(search.toLowerCase()) ||
      row.orderId.toLowerCase().includes(search.toLowerCase()) ||
      row.partner.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All status' || row.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const toggleRow = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map(r => r.id));

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search payments..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <CustomSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={['All status', 'Pending', 'Disputed', 'Flagged', 'Cleared']}
        />
      </div>
      <button className={styles.refreshBtn} onClick={() => { setSearch(''); setStatusFilter('All status'); load(); }}>
        <LuRefreshCw size={13} /> Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Payments">
      <Drawer
        isOpen={!!activePayment}
        title={activePayment?.paymentId || 'Payment'}
        onClose={() => { setActive(null); setStatusError(''); setSelectedStatus(''); }}
        footer={
          <button
            className={styles.btnCleared}
            disabled={statusSaving || !selectedStatus || selectedStatus === activePayment?.status}
            onClick={async () => {
              setStatusSaving(true);
              setStatusError('');
              try {
                await updatePayment(activePayment.id, { status: selectedStatus });
                setActive(prev => ({ ...prev, status: selectedStatus }));
                setSelectedStatus('');
                load();
              } catch (err) {
                setStatusError(err.message);
              } finally {
                setStatusSaving(false);
              }
            }}
          >
            {statusSaving ? 'Saving...' : 'Update status'}
          </button>
        }
      >
        {activePayment && (
          <>
            <div className={styles.drawerCard}>
              <div className={styles.drawerGrid}>
                <div>
                  <p className={styles.drawerLabel}>Payment ID</p>
                  <p className={styles.drawerValue}>{activePayment.paymentId}</p>
                </div>
                <div>
                  <p className={styles.drawerLabel}>Order ID</p>
                  <p className={styles.drawerValue}>{activePayment.orderId}</p>
                </div>
                <div>
                  <p className={styles.drawerLabel}>Direction</p>
                  <p className={styles.drawerValue}>{activePayment.direction}</p>
                </div>
                <div>
                  <p className={styles.drawerLabel}>Partner</p>
                  <p className={styles.drawerValue}>{activePayment.partner}</p>
                </div>
                <div>
                  <p className={styles.drawerLabel}>Amount</p>
                  <p className={styles.drawerValue}>{activePayment.amount}</p>
                </div>
              </div>
            </div>

            <div className={styles.drawerCard}>
              <p className={styles.drawerLabel}>Update status</p>
              <select
                className={styles.statusSelect}
                value={selectedStatus || activePayment.status}
                disabled={statusSaving}
                onChange={e => { setSelectedStatus(e.target.value); setStatusError(''); }}
              >
                <option value="pending">Pending</option>
                <option value="disputed">Disputed</option>
                <option value="flagged">Flagged</option>
                <option value="cleared">Cleared</option>
              </select>
              {statusError && <p className={styles.clearError}>{statusError}</p>}
            </div>
          </>
        )}
      </Drawer>

      {/* Stat cards */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-4"><StatCard label="Total payments"  value={String(totalCount).padStart(2, '0')} /></div>
        <div className="col-6 col-md-4"><StatCard label="Pending"         value={String(pendingCount).padStart(2, '0')} /></div>
        <div className="col-6 col-md-4"><StatCard label="Approved"        value={String(approvedCount).padStart(2, '0')} /></div>
      </div>

      {/* Table */}
      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                />
              </th>
              <th>Payment ID</th>
              <th>Order ID</th>
              <th>Direction</th>
              <th>Partner</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className={styles.loadingCell}>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className={styles.loadingCell}>No payments found.</td></tr>
            )}
            {!loading && filtered.map(row => (
              <tr key={row.id} onClick={() => setActive(row)} style={{ cursor: 'pointer' }}>
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
                <td className={styles.orderId}>{row.paymentId}</td>
                <td>{row.orderId}</td>
                <td>{row.direction}</td>
                <td>{row.partner}</td>
                <td>{row.amount}</td>
                <td><StatusBadge status={capitalize(row.status)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
